import {FC, useEffect, useMemo} from 'react';
import {
    Alert,
    Box,
    CircularProgress,
    Grid,
    Stack,
    Typography,
} from '@mui/material';
import {useNavigate, useParams} from 'react-router-dom';
import {useAiImageGroupDetail, useAiImageGroups} from '../hooks/useAiImages';
import {AiImageGroupList} from '../components/ai-image/AiImageGroupList';
import {AiImageGroupHeader} from '../components/ai-image/AiImageGroupHeader';
import {AiImageRequestForm} from '../components/ai-image/AiImageRequestForm';
import {AiImageRequestCard} from '../components/ai-image/AiImageRequestCard';

const AiImagesPage: FC = () => {
    const params = useParams<{groupId?: string}>();
    const navigate = useNavigate();

    const groupIdFromUrl = useMemo(() => {
        if (!params.groupId) return undefined;
        const n = Number(params.groupId);
        return Number.isFinite(n) ? n : undefined;
    }, [params.groupId]);

    const groupsQuery = useAiImageGroups();
    const groups = useMemo(() => groupsQuery.data ?? [], [groupsQuery.data]);

    // URL groupId 우선 + 없으면 첫 그룹 자동 선택 (URL에 반영)
    useEffect(() => {
        if (groupsQuery.isLoading) return;
        if (groups.length === 0) return;
        if (groupIdFromUrl && groups.some((g) => g.id === groupIdFromUrl)) return;
        // URL 미지정이거나 유효하지 않은 id → 첫 그룹으로 리다이렉트
        navigate(`/ai-images/groups/${groups[0].id}`, {replace: true});
    }, [groupsQuery.isLoading, groups, groupIdFromUrl, navigate]);

    const detailQuery = useAiImageGroupDetail(groupIdFromUrl);
    const detail = detailQuery.data;

    const handleSelect = (id: number) => {
        navigate(`/ai-images/groups/${id}`);
    };

    const handleDeleted = () => {
        // 삭제 후 첫 그룹으로 또는 빈 상태로
        navigate('/ai-images', {replace: true});
    };

    return (
        <Box sx={{p: 3}}>
            <Typography variant="h5" fontWeight={700} sx={{mb: 2}}>
                AI 이미지 생성
            </Typography>
            <Grid container spacing={2}>
                <Grid size={{xs: 12, md: 4}}>
                    <AiImageGroupList
                        groups={groups}
                        isLoading={groupsQuery.isLoading}
                        selectedGroupId={groupIdFromUrl}
                        onSelect={handleSelect}
                    />
                </Grid>
                <Grid size={{xs: 12, md: 8}}>
                    {!groupIdFromUrl ? (
                        groups.length === 0 && !groupsQuery.isLoading ? (
                            <Alert severity="info">
                                좌측에서 새 그룹을 만들어 시작하세요.
                            </Alert>
                        ) : (
                            <Box sx={{display: 'flex', justifyContent: 'center', py: 6}}>
                                <CircularProgress size={28} />
                            </Box>
                        )
                    ) : detailQuery.isLoading && !detail ? (
                        <Box sx={{display: 'flex', justifyContent: 'center', py: 6}}>
                            <CircularProgress size={28} />
                        </Box>
                    ) : detailQuery.isError ? (
                        <Alert severity="error">
                            그룹을 불러오지 못했습니다: {(detailQuery.error as Error)?.message ?? ''}
                        </Alert>
                    ) : detail ? (
                        <Stack spacing={2}>
                            <AiImageGroupHeader
                                group={detail.group}
                                onDeleted={handleDeleted}
                            />
                            <AiImageRequestForm group={detail.group} />
                            <Box>
                                <Typography variant="subtitle1" fontWeight={700} sx={{mb: 1}}>
                                    요청 이력 ({detail.requests.length})
                                </Typography>
                                {detail.requests.length === 0 ? (
                                    <Alert severity="info">
                                        아직 요청이 없습니다. 위 폼에서 첫 요청을 생성해 보세요.
                                    </Alert>
                                ) : (
                                    <Stack spacing={1.5}>
                                        {detail.requests.map((req) => (
                                            <AiImageRequestCard
                                                key={req.id}
                                                request={req}
                                                groupName={detail.group.name}
                                            />
                                        ))}
                                    </Stack>
                                )}
                            </Box>
                        </Stack>
                    ) : null}
                </Grid>
            </Grid>
        </Box>
    );
};

export default AiImagesPage;
